/* eslint-disable max-lines-per-function */
import React, { useState, useMemo } from 'react';
import {
  Box,
  Select,
  MenuItem,
  InputLabel,
  ListSubheader,
  TextField,
  InputAdornment,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { ReactComponent as SearchGlass } from '../../Assets/images/svg/dar_search.svg';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PropTypes from 'prop-types';
import './multiSelectDropdown.scss';
import { SEARCH } from '../../Constants/strings';

const containsText = (text, searchText) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

const SearchMultiSelect = ({
  label,
  options = [],
  placeholder,
  onChange,
  value,
  multiSelectCss,
  disabled = false,
  disabledOptions = [],
}) => {
  const [searchText, setSearchText] = useState('');
  const isMultiple = Array.isArray(value); // Check if value is an array
  const disabledIds = useMemo(
    () => new Set((disabledOptions || []).map((opt) => opt.id)),
    [disabledOptions],
  );
  const displayedOptions = useMemo(
    () => options.filter((option) => containsText(option.name, searchText)),
    [searchText, options],
  );

  return (
    <Box className={`common-select-root ${multiSelectCss}`}>
      <InputLabel className="select-input-label">{label}</InputLabel>
      <Select
        multiple={isMultiple}
        className="common-select"
        MenuProps={{
          PaperProps: {
            className: 'multiple-dropdown-paper',
          },
          autoFocus: false,
        }}
        id="search-multi-select"
        IconComponent={KeyboardArrowDownIcon}
        value={value}
        onChange={onChange}
        onClose={() => setSearchText('')}
        displayEmpty={true}
        renderValue={(selected) => {
          if (!selected || (isMultiple && selected.length === 0)) {
            return <span className="select-placeholder">{placeholder}</span>;
          } else if (isMultiple) {
            return selected.map((option) => option.name).join(', ');
          } else {
            return selected.name;
          }
        }}
        fullWidth
        size="small"
        disabled={disabled}
      >
        <ListSubheader className="select-list">
          <TextField
            className="select-text-field"
            size="small"
            autoFocus
            placeholder={SEARCH}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchGlass />
                </InputAdornment>
              ),
            }}
            onChange={(event) => setSearchText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Escape') {
                event.stopPropagation();
              }
            }}
          />
        </ListSubheader>
        {displayedOptions.map((option) => {
          const isDisabledOption = disabledIds.has(option.id);
          return (
            <MenuItem
              className="select-menu-item"
              key={option.id}
              value={option}
              disabled={isDisabledOption}
            >
              <Checkbox
                className="select-checkbox"
                checked={value.some((selected) => selected.id === option.id)}
                disabled={isDisabledOption}
              />
              <ListItemText primary={option.name} />
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
};

SearchMultiSelect.propTypes = {
  options: PropTypes.array,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.any,
  multiSelectCss: PropTypes.string,
  disabled: PropTypes.bool,
  disabledOptions: PropTypes.array,
};

export default SearchMultiSelect;
